import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../../../shared/components';
import { listAiUseCaseActivity, AiUseCaseActivityItem } from '../services/aiPromptConfigService';
import styles from './UseCaseActivityPanel.module.css';

const PAGE_SIZE = 20;

const formatDateTime = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const badgeClassName = (action: string): string => {
  if (action === 'created') {
    return styles.created;
  }

  if (action === 'deleted') {
    return styles.deleted;
  }

  return styles.updated;
};

interface UseCaseActivityPanelProps {
  useCaseId: string;
  onClose: () => void;
}

export const UseCaseActivityPanel: React.FC<UseCaseActivityPanelProps> = ({ useCaseId, onClose }) => {
  const { t } = useTranslation(['admin', 'common']);
  const [items, setItems] = React.useState<AiUseCaseActivityItem[]>([]);
  const [page, setPage] = React.useState(1);
  const [totalCount, setTotalCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const titleId = `use-case-activity-title-${useCaseId}`;

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    void listAiUseCaseActivity(useCaseId, page, PAGE_SIZE)
      .then((result) => {
        if (cancelled) {
          return;
        }

        setItems(result.items);
        setTotalCount(result.totalCount);
      })
      .catch((loadError: unknown) => {
        if (cancelled) {
          return;
        }

        setError(loadError instanceof Error ? loadError.message : t('aiSettings.activityFailed', { ns: 'admin' }));
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [page, t, useCaseId]);

  React.useEffect(() => {
    setPage(1);
  }, [useCaseId]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return createPortal(
    <div className={styles.overlay} role="presentation" onClick={onClose}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.head}>
          <div>
            <h3 id={titleId} className={styles.title}>{t('aiSettings.activityTitle', { ns: 'admin' })}</h3>
            <p className={styles.help}>{t('aiSettings.activityHelp', { ns: 'admin' })}</p>
          </div>
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label={t('close', { ns: 'common' })}
          >
            ×
          </button>
        </div>

        {error ? <div className={styles.error}>{error}</div> : null}
        {loading ? <Spinner text={t('aiSettings.activityLoading', { ns: 'admin' })} /> : null}
        {!loading && !error && items.length === 0 ? (
          <div className={styles.empty}>{t('aiSettings.activityEmpty', { ns: 'admin' })}</div>
        ) : null}
        {!loading && items.length > 0 ? (
          <ul className={styles.list}>
            {items.map((item) => {
              const actor = item.isSystem
                ? t('aiSettings.activitySystem', { ns: 'admin' })
                : item.actorName || t('aiSettings.activityUnknown', { ns: 'admin' });
              const detail = item.detail || 'prompt';
              const label = t(`aiSettings.activityEvents.${item.action}.${detail}`, {
                ns: 'admin',
                defaultValue: item.action,
              });
              return (
                <li key={item.id} className={styles.row}>
                  <span className={`${styles.badge} ${badgeClassName(item.action)}`}>{label}</span>
                  <span className={styles.meta}>{actor}</span>
                  <span className={styles.meta}>{formatDateTime(item.createdAt)}</span>
                </li>
              );
            })}
          </ul>
        ) : null}
        {totalCount > PAGE_SIZE ? (
          <div className={styles.pager}>
            <button type="button" className="btn btn-secondary" disabled={page <= 1 || loading} onClick={() => setPage((current) => Math.max(1, current - 1))}>
              {t('previous', { ns: 'common' })}
            </button>
            <span>{page} / {totalPages}</span>
            <button type="button" className="btn btn-secondary" disabled={page >= totalPages || loading} onClick={() => setPage((current) => Math.min(totalPages, current + 1))}>
              {t('next', { ns: 'common' })}
            </button>
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
};
